import { useDrag, useDrop } from 'react-dnd'

import DragIndicatorIcon from '@/assets/icons/icon-drag-indicator.svg'
import InfoIcon from '@/assets/icons/icon-info.svg'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import { unescapeHtmlText } from '@/modules/chatting/ui/chat-window/fan-info/utils'
import { ListMembersAvatars } from '@/modules/chatting/ui/chat-window/lists/members-avatars'
import { IconButton } from '@/modules/chatting/ui/components/icon-button'
import { classNames } from '@/utils/helpers'

import { useCollectionContext } from '../CollectionProvider'
import type { CollectionsItem } from './types'

interface Props {
  list: CollectionsItem
  onClick: (listId: CollectionsItem['id']) => void
  onMove: (origin: CollectionsItem, target: CollectionsItem) => void
  isCustomizeOrderModeActive?: boolean
}

export const ListItem = ({
  list,
  onClick,
  onMove,
  isCustomizeOrderModeActive,
}: Props) => {
  const { collection } = useCollectionContext()
  const isCustomList = list?.type === 'custom'

  const [, dropRef] = useDrop({
    accept: 'list',
    drop: (origin: CollectionsItem) => {
      if (isCustomList && isCustomizeOrderModeActive) {
        onMove(origin, list)
      }
    },
  })

  const [{ isDragging }, dragRef, previewRef] = useDrag({
    item: () => list,
    type: 'list',
    canDrag: isCustomList && isCustomizeOrderModeActive,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
      canDrag: monitor.canDrag(),
    }),
  })

  const isSelected = collection?.id === list.id

  return (
    <div className="mb-2 max-w-full space-y-2">
      <div
        ref={(ref) => {
          dropRef(ref)
          previewRef(ref)
        }}
        className={classNames(
          'flex cursor-pointer justify-between gap-3 rounded-lg px-3 py-2 hover:bg-primary-50 dark:hover:bg-primary-900',
          {
            'bg-primary-50 dark:bg-primary-900': isSelected,
          },
        )}
        onClick={() => onClick(list.id)}
      >
        <div className="flex items-center gap-3">
          {isCustomList && isCustomizeOrderModeActive && (
            <IconButton
              ref={dragRef}
              icon={DragIndicatorIcon}
              className={classNames('cursor-grab text-secondary-300', {
                'cursor-grabbing': isDragging,
              })}
            />
          )}

          <div className="space-y-0.5 overflow-hidden">
            <p className="overflow-hidden text-ellipsis whitespace-nowrap">
              {unescapeHtmlText(list.name)}
            </p>
            <p className="flex items-center gap-2 text-ellipsis whitespace-nowrap text-sm font-medium leading-[22px] text-secondary-300">
              <UsersAndPostsInfo
                list={list}
                isSelected={isSelected}
              />
            </p>
          </div>
        </div>
        <ListMembersAvatars
          size={50}
          members={list.users}
          avatarClassName="group-hover:border-primary-50 dark:group-hover:border-secondary-600"
        />
      </div>
      <div className="border-b" />
    </div>
  )
}

interface UsersAndPostsInfoProps {
  list: CollectionsItem
  isSelected: boolean
}

const UsersAndPostsInfo = ({ list, isSelected }: UsersAndPostsInfoProps) => {
  if (!list.usersCount && !list.postsCount) {
    return 'Empty'
  }

  return (
    <>
      {!!list.usersCount && (
        <span>
          {list.usersCount} {list.usersCount > 1 ? 'users' : 'user'}
        </span>
      )}
      {!!list.usersCount && isSelected && (
        <Tooltip
          content={`All ${list.name} without filters applied`}
          contentClassName="min-w-max text-title"
          className="flex items-center justify-center gap-1"
          side="right"
        >
          <InfoIcon className="h-4 w-4 flex-shrink-0" />
        </Tooltip>
      )}
      {!!list.postsCount && (
        <>
          <span>&#x2022;</span>
          <span className="block overflow-hidden text-ellipsis whitespace-nowrap">
            {list.postsCount} {list.postsCount > 1 ? 'posts' : 'post'}
          </span>
        </>
      )}
    </>
  )
}
